import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'

/** Le serveur refuse un motif de moins de cinq caractères (espaces retirés). */
const MIN_REASON = 5

/**
 * « On s'est trompé » — défaire une publication, une proclamation, une
 * révélation.
 *
 * Toujours en deux temps : un bouton discret, puis un encadré qui dit ce qui
 * va disparaître (et ce qui ne se rattrape pas, comme un e-mail déjà parti)
 * et demande le motif. Le motif est obligatoire : il part au journal d'audit
 * et dans l'alerte de l'équipe — retirer un résultat que la salle a peut-être
 * vu est une décision qui se justifie.
 *
 * Le composant n'appelle rien lui-même : `onConfirm` reçoit le motif, le
 * parent fait la requête et affiche son erreur.
 */
export function UndoPublication({
  label,
  confirmLabel,
  warnings,
  busy,
  onConfirm,
  disabled = false,
  className,
}: {
  /** Le bouton qui ouvre l'encadré. */
  label: string
  /** Le bouton qui confirme, dans l'encadré. */
  confirmLabel: string
  /** Les conséquences, une phrase par ligne ; les plus graves d'abord. */
  warnings: string[]
  busy: boolean
  /** Reçoit le motif, déjà nettoyé. Renvoie une promesse pour fermer au succès. */
  onConfirm: (reason: string) => Promise<unknown>
  disabled?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const fieldId = useId()
  const cleaned = reason.trim()

  if (!open) {
    return (
      <Button
        className={cn(className)}
        size="sm"
        variant="outline"
        disabled={disabled || busy}
        onClick={() => {
          setReason('')
          setOpen(true)
        }}
      >
        {label}
      </Button>
    )
  }

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border border-state-warning/40 bg-state-warning/5 px-4 py-3',
        className,
      )}
    >
      <ul className="space-y-1">
        {warnings.map((warning) => (
          <li key={warning} className="text-caption text-state-warning">
            {warning}
          </li>
        ))}
      </ul>
      <div>
        <label htmlFor={fieldId} className="text-caption text-content-secondary">
          {t('admin.undo.reasonLabel')}
        </label>
        <textarea
          id={fieldId}
          value={reason}
          rows={2}
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('admin.undo.reasonPlaceholder')}
          className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
        />
        <p className="mt-1 text-caption text-content-muted">{t('admin.undo.reasonHint')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="ember"
          disabled={busy || cleaned.length < MIN_REASON}
          onClick={() => {
            // Fermé au succès seulement : sur un refus, le motif reste saisi
            // et l'erreur s'affiche sous l'encadré.
            onConfirm(cleaned).then(
              () => setOpen(false),
              () => undefined,
            )
          }}
        >
          {confirmLabel}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
          {t('admin.cancel')}
        </Button>
      </div>
    </div>
  )
}
